import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui/button';
import { RefreshCw, X } from 'lucide-react';
import { useState } from 'react';

/**
 * Best-effort teardown for the edge case where the SW lifecycle doesn't hand
 * over: unregister every worker, drop every cache, then reload. User-initiated
 * (behind the Update button) so there's no reload-loop guard to respect.
 */
async function forceHardReload() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // best-effort — reload regardless
  }
  window.location.reload();
}

/**
 * Shows a non-intrusive banner when a new version of the app is available.
 * Clicking "Update" activates the waiting service worker via SKIP_WAITING;
 * vite-plugin-pwa then reloads the page on `controllerchange` so the new SW
 * takes control and serves its freshly-precached assets.
 */
export function ReloadPrompt() {
  const [updating, setUpdating] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // Check for updates every 60 seconds
      if (registration) {
        setInterval(() => { registration.update(); }, 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      // Promote the WAITING service worker (posts SKIP_WAITING). It activates,
      // claims the page, and vite-plugin-pwa reloads on `controllerchange`.
      // This is the reliable path — manually unregistering does NOT skip-waiting,
      // so the new worker stays "waiting" and the old one keeps controlling the tab.
      await updateServiceWorker(true);

      // Safety net: if no waiting worker ever takes over (so `controllerchange`
      // never fires and the page doesn't reload), force a hard recovery. If the
      // reload above happened first, the page is gone and this timer never runs.
      setTimeout(() => { void forceHardReload(); }, 3000);
    } catch (err) {
      console.error('SW update failed, forcing hard reload:', err);
      await forceHardReload();
    }
  };

  if (!needRefresh) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg">
        <RefreshCw className={`h-4 w-4 text-primary shrink-0${updating ? ' animate-spin' : ''}`} />
        <p className="text-sm font-medium">A new version is available</p>
        <Button
          size="sm"
          className="gap-1.5"
          disabled={updating}
          onClick={handleUpdate}
        >
          {updating ? 'Updating…' : 'Update'}
        </Button>
        <button
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setNeedRefresh(false)}
          disabled={updating}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
